import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDecimal } from '@app/common';
import styles from './compound-interest-table.module.css';
import { Table } from '../../../../components/app/table/table';
import { TableHead } from '../../../../components/app/table/head/table-head';
import { TableBody } from '../../../../components/app/table/body/table-body';
import { TableRow } from '../../../../components/app/table/row/table-row';
import { TableCell } from '../../../../components/app/table/cell/table-cell';
import { Text } from '../../../../components/app/text/text';
import { Tooltip } from '../../../../components/app/tooltip/tooltip';
import { CompoundInterestYearResult, CompoundInterestParams } from '../../../../utils/compound-interest/types';

interface CompoundInterestTableProps {
    results: CompoundInterestYearResult[];
    params: CompoundInterestParams;
}

export const CompoundInterestTable: React.FC<CompoundInterestTableProps> = ({ results, params }) => {
    const { t } = useTranslation();
    const { currency } = params;
    const baseYear = new Date().getFullYear();
    const frameRef = useRef<HTMLDivElement>(null);
    const [isHeaderSticky, setIsHeaderSticky] = useState(false);

    useEffect(() => {
        const frame = frameRef.current;
        const head = frame?.querySelector('thead');
        if (!frame || !head) return undefined;
        let animationFrame = 0;

        const update = () => {
            const headerStyle = window.getComputedStyle(head);
            const offset = parseFloat(headerStyle.top);
            const bounds = frame.getBoundingClientRect();
            const rows = frame.querySelectorAll('tbody tr');
            const releaseRow = rows[Math.max(0, rows.length - 5)];
            const releaseTop = releaseRow?.getBoundingClientRect().top ?? bounds.bottom;
            const isSticky = headerStyle.position === 'sticky' && bounds.top < offset;
            const shift = isSticky ? Math.min(0, releaseTop - offset - head.offsetHeight) : 0;
            head.style.transform = shift < 0 ? `translateY(${shift}px)` : '';
            setIsHeaderSticky(isSticky && releaseTop > 0);
        };
        const scheduleUpdate = () => {
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(update);
        };

        window.addEventListener('scroll', scheduleUpdate, { passive: true });
        window.addEventListener('resize', scheduleUpdate);
        const observer = new ResizeObserver(scheduleUpdate);
        observer.observe(frame);
        update();
        return () => {
            cancelAnimationFrame(animationFrame);
            observer.disconnect();
            window.removeEventListener('scroll', scheduleUpdate);
            window.removeEventListener('resize', scheduleUpdate);
        };
    }, []);

    return (
        <section aria-label={t('pages.compoundInterest.table.title')}>
            <h2 className="mb-[2.4rem] text-[clamp(2.8rem,4vw,4.4rem)] leading-[1.15] font-semibold tracking-[-0.035em] text-brand-secondary md:mb-[3.2rem]">
                {t('pages.compoundInterest.table.title')}
            </h2>
            <div ref={frameRef} className={styles.frame} data-sticky={isHeaderSticky}>
                <Table mobileBreakUnder="xl" containerMarginBottom="none" containerOverflow={false}>
                    <TableHead>
                        <TableRow>
                            <TableCell width="10%">{t('pages.compoundInterest.table.year')}</TableCell>
                            <TableCell width="15%">{t('pages.compoundInterest.table.totalDeposited')}</TableCell>
                            <TableCell width="15%">{t('pages.compoundInterest.table.interestEarned')}</TableCell>
                            <TableCell width="15%">{t('pages.compoundInterest.table.totalValue')}</TableCell>
                            <TableCell width="15%">
                                {params.taxRate === 0 ? (
                                    <Tooltip
                                        icon
                                        content={t('pages.compoundInterest.calculator.zeroTaxRateTooltip')}
                                        position="top"
                                    >
                                        {t('pages.compoundInterest.table.taxAmount')}
                                    </Tooltip>
                                ) : (
                                    t('pages.compoundInterest.table.taxAmount')
                                )}
                            </TableCell>
                            <TableCell width="15%">
                                {params.taxRate === 0 ? (
                                    <Tooltip
                                        icon
                                        content={t('pages.compoundInterest.calculator.zeroTaxRateTooltip')}
                                        position="top"
                                    >
                                        {t('pages.compoundInterest.table.netValue')}
                                    </Tooltip>
                                ) : (
                                    t('pages.compoundInterest.table.netValue')
                                )}
                            </TableCell>
                            <TableCell width="15%">{t('pages.compoundInterest.table.realValue')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map((result) => (
                            <TableRow key={result.year}>
                                <TableCell label={t('pages.compoundInterest.table.year')}>
                                    <Text bold>{baseYear + result.year}</Text>
                                </TableCell>
                                <TableCell label={t('pages.compoundInterest.table.totalDeposited')}>
                                    <Text variant="secondary" bold>
                                        {formatDecimal(result.totalDeposited)} {currency}
                                    </Text>
                                </TableCell>
                                <TableCell label={t('pages.compoundInterest.table.interestEarned')}>
                                    <Text variant="positive" bold>
                                        {formatDecimal(result.interestEarned)} {currency}
                                    </Text>
                                </TableCell>
                                <TableCell label={t('pages.compoundInterest.table.totalValue')}>
                                    <Text bold>
                                        {formatDecimal(result.totalValue)} {currency}
                                    </Text>
                                </TableCell>
                                <TableCell label={t('pages.compoundInterest.table.taxAmount')}>
                                    <Text variant={result.taxAmount > 0 ? 'negative' : 'gray'} bold>
                                        {result.taxAmount > 0 ? '-' : ''}
                                        {formatDecimal(result.taxAmount)} {currency}
                                    </Text>
                                </TableCell>
                                <TableCell label={t('pages.compoundInterest.table.netValue')}>
                                    <Text bold>
                                        {formatDecimal(result.netValue)} {currency}
                                    </Text>
                                </TableCell>
                                <TableCell label={t('pages.compoundInterest.table.realValue')}>
                                    <Text variant="secondary" bold>
                                        {formatDecimal(result.realValue)} {currency}
                                    </Text>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </section>
    );
};
